import { ApiService } from '@/services';
import { Translation, DataLoaderParams, PaginatedResponse } from '@/types';
import gql from 'graphql-tag';
import { Path } from 'vue-i18n';
import { LocalStorageService } from '@/services';
import Config from '@/config';
import store from '@/store';
import { APP_ADD_TRANSLATION } from '@/store/mutypes';

export class TranslationService extends ApiService {
  public async getTranslations(
    translationFilters: any
  ): Promise<Translation[]> {
    const query = gql`
      query searchTranslations($filters: TranslationSearchFilters) {
        searchTranslations(filters: $filters) {
          id
          key
          locale
          group
          namespace
          value
          description
          created_at
          updated_at
        }
      }
    `;

    return await this.query<Translation[]>(
      { query, variables: { filters: translationFilters } },
      'searchTranslations'
    );
  }

  public async getLocales(): Promise<String[]> {
    const query = gql`
      query locales {
        locales
      }
    `;
    return await this.query<String[]>({ query }, 'locales');
  }

  public async upsertTranslation(
    translation: Translation
  ): Promise<Translation> {
    const mutation = gql`
      mutation upsertTranslation($input: TranslationInput) {
        upsertTranslation(input: $input) {
          id
          key
          locale
          group
          namespace
          value
          description
          created_at
          updated_at
        }
      }
    `;
    return this.mutate(
      { mutation, variables: { input: translation } },
      'upsertTranslation'
    );
  }

  public async updateTranslation(
    translation: Translation
  ): Promise<Translation> {
    const mutation = gql`
      mutation updateTranslation($input: TranslationInput) {
        updateTranslation(input: $input) {
          id
          key
          locale
          group
          namespace
          value
          description
          created_at
          updated_at
        }
      }
    `;
    return this.mutate(
      { mutation, variables: { input: translation } },
      'updateTranslation'
    );
  }

  public async createTranslation(
    translation: Translation
  ): Promise<Translation> {
    const mutation = gql`
      mutation createTranslation($input: TranslationInput) {
        createTranslation(input: $input) {
          id
          key
          locale
          group
          namespace
          value
          description
          created_at
          updated_at
        }
      }
    `;
    return this.mutate(
      { mutation, variables: { input: translation } },
      'createTranslation'
    );
  }

  private async getTranslationFromNetwork(filters: {
    locale: string;
    key: string;
    namespace: string;
    group: string;
  }): Promise<Translation> {
    const query = gql`
      query translation($filters: TranslationFilters) {
        translation(filters: $filters) {
          key
          value
          namespace
          group
          locale
        }
      }
    `;

    return this.pubQuery<Translation>(
      { query, variables: { filters } },
      'translation'
    );
  }

  public async getTranslation(locale: string, key: Path) {
    const item = key;
    const cacheKey = `${locale}.${item}`;
    const cached = LocalStorageService.get(cacheKey);
    if (cached) {
      const nowTime = new Date().getTime();
      if (cached && cached.expiration > nowTime) {
        return cached.value;
      }
    }

    const [transGroup, transKey] = item.split('.');
    const translation = await this.getTranslationFromNetwork({
      namespace: 'upservice',
      locale,
      key: transKey,
      group: transGroup
    });
    if (!translation) {
      throw new Error('Translation not found');
    }

    const minutesToCache = Config.TRANSLATION_CACHE_MINUTES;
    const expiration = new Date().getTime() + minutesToCache * 60000;
    const cacheItem = {
      value: translation.value,
      expiration
    };
    LocalStorageService.set(cacheKey, cacheItem);

    store.commit(APP_ADD_TRANSLATION, { key, value: translation.value });

    return translation.value;
  }
}

export const translationSvc: TranslationService = new TranslationService();
